import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { TooltipModule } from 'primeng/tooltip';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { ClampedTextComponent } from '@workbuddy/ui/components/clamped.text';
import { SorDescriptionExpansionService } from '../../services/item.description.expansion.service';
import { resolveItemFromParams } from '@workbuddy/entities/shared/items';

/**
 * Description cell for the SOR board: the description text plus the item note shown as a
 * "Additional Instruction" line. SOR board only (manifest-gated). The note line respects the hideNote
 * tenant setting.
 *
 * Description clamp/more-less is {@link ClampedTextComponent}; expand state is persisted by item id
 * in {@link SorDescriptionExpansionService} so AG redraws do not snap an open paragraph shut.
 * Hovering the Additional Instruction line opens a rich dark tooltip (PrimeNG `autoHide: false`).
 */
@Component({
  selector: 'item-description-cell-renderer',
  standalone: true,
  imports: [TooltipModule, ClampedTextComponent],
  template: `
    <div class="description-cell">
      <w-clamped-text [text]="$description()" [maxLines]="2" [expanded]="$expanded()" (expandedChange)="onExpandedChange($event)" [stopClickPropagation]="true" />

      @if ($showNote()) {
        <span
          class="additional-instruction"
          [pTooltip]="additionalInstructionTip"
          [autoHide]="false"
          tooltipPosition="bottom"
          tooltipStyleClass="wb-dark-tooltip"
          appendTo="body"
          (click)="$event.stopPropagation()">
          <span class="additional-instruction-icon"><i class="fa-regular fa-memo" aria-hidden="true"></i></span>
          <span class="additional-instruction-text">{{ $note() }}</span>
        </span>

        <ng-template #additionalInstructionTip>
          <div class="additional-instruction-card">
            <div class="additional-instruction-title">Additional Instruction</div>
            <div class="additional-instruction-body">{{ $note() }}</div>
          </div>
        </ng-template>
      }
    </div>
  `,
  styleUrls: ['./item.description.cell.renderer.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemDescriptionCellRendererComponent implements ICellRendererAngularComp {

  private readonly generalSetting = inject(GeneralSettingService);
  private readonly expansion = inject(SorDescriptionExpansionService);

  private readonly hideNote = !!this.generalSetting.generalSettings?.items?.hideNote;

  private itemId = '';

  protected readonly $description = signal<string>('');
  protected readonly $note = signal<string>('');
  protected readonly $expanded = signal<boolean>(false);

  protected readonly $showNote = computed(() => !this.hideNote && !!this.$note());

  public agInit(params: ICellRendererParams): void {

    this.read(params);
  }

  public refresh(params: ICellRendererParams): boolean {

    this.read(params);
    return true;
  }

  protected onExpandedChange(expanded: boolean): void {

    this.$expanded.set(expanded);
    this.expansion.set(this.itemId, expanded);
  }

  private read(params: ICellRendererParams): void {

    const item = resolveItemFromParams(params) as { _id?: string; description?: string; note?: string } | undefined;
    this.itemId = item?._id ?? '';
    this.$description.set(item?.description ?? '');
    this.$note.set(item?.note ?? '');
    this.$expanded.set(this.expansion.isExpanded(this.itemId));
  }
}

/**
 * Column value for the Description cell — description plus note, used as the AG value so a note
 * (Additional Instruction) edit refreshes the cell. The column field is `description` alone, which a
 * note-only change leaves unchanged, so AG would otherwise never call refresh().
 */
export function itemDescriptionCellValue(params: { data?: unknown; colDef?: { field?: string } }): string {

  const item = resolveItemFromParams(params as unknown as ICellRendererParams) as { description?: string; note?: string } | undefined;
  if (!item) return '';

  return `${item.description ?? ''}|${item.note ?? ''}`;
}
